import { Fragment, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, ArrowDown, CheckCircle2, Loader2, ShieldCheck, Check, Plus, Minus,
  Stethoscope, Store, GraduationCap, Network, Scale, HeartPulse, Venus, Mars,
  Brain, Sparkles, ShieldPlus, Droplets, Salad, Plane, ClipboardList,
  Telescope, Building2, FileSignature, Users, MonitorSmartphone, MapPin, Lock,
  Video, CalendarCheck, UserCircle, Pill, Search, MessageSquare, BarChart3,
  Settings2, Rocket, Download,
} from "lucide-react";
import { Reveal, MaskedLine } from "../components/Reveal";
import Seo from "../components/Seo";
import PrivacyLink from "../components/PrivacyLink";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl px-4 py-3.5 text-sm text-[#0B192C] placeholder:text-[#94A3B8] outline-none transition-colors duration-300 focus:border-[#10B981] focus:bg-white";
const labelCls = "block text-xs font-bold uppercase tracking-wider text-[#475569] mb-2";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

/* Admin-replaceable site image (falls back to bundled default) */
const useSiteContent = () => {
  const [content, setContent] = useState(null);
  useEffect(() => {
    axios.get(`${API}/site/content`).then((r) => setContent(r.data)).catch(() => {});
  }, []);
  return content;
};

const SiteImg = ({ content, slot, fallback, alt, className, testid }) => {
  const [err, setErr] = useState(false);
  const custom = content?.[`${slot}_custom`];
  const src = custom && !err ? `${API}/site/image/${slot}` : fallback;
  return (
    <img src={src} onError={() => setErr(true)} alt={alt} loading="lazy" data-testid={testid} className={className} />
  );
};

const HIGHLIGHTS = [
  { icon: Stethoscope, color: "#10B981", label: "Pharmacist-Led Private Healthcare" },
  { icon: Store, color: "#06B6D4", label: "Community Pharmacy Partnerships" },
  { icon: GraduationCap, color: "#F59E0B", label: "Clinical Training & Support" },
  { icon: Network, color: "#EF4444", label: "National Clinic Network Vision" },
];

const SERVICES = [
  { icon: Scale, color: "#10B981", title: "Weight Management & Metabolic Health",
    tagline: "Medical weight management with structured monitoring and follow-up.",
    items: ["Medical weight management", "GLP-1 treatment assessment where clinically appropriate", "Tirzepatide-based weight management pathways", "Weight loss monitoring and follow-up", "Lifestyle and nutritional support", "Cardiometabolic risk assessment"] },
  { icon: Mars, color: "#06B6D4", title: "Men's Health",
    tagline: "Confidential men's health consultations and treatment pathways.",
    items: ["Testosterone deficiency assessment", "Testosterone replacement therapy (TRT), subject to specialist pathways and monitoring", "Erectile dysfunction", "Premature ejaculation", "Hair loss", "Men's sexual health", "Prostate health advice and appropriate referral", "Male hormone health"] },
  { icon: Venus, color: "#EF4444", title: "Women's Health",
    tagline: "Menopause, hormonal and reproductive health support.",
    items: ["Menopause and perimenopause", "Hormone replacement therapy assessment", "Period and menstrual health", "Contraception consultations", "Women's sexual health", "Reproductive health advice", "Hair loss and skin concerns"] },
  { icon: Brain, color: "#F59E0B", title: "ADHD & Neurodevelopmental Services",
    tagline: "Specialist-led assessment pathways with ongoing treatment support.",
    items: ["ADHD screening and initial consultations", "Referral and specialist assessment pathways", "ADHD diagnostic services where appropriately authorised and specialist-led", "Medication initiation where clinically and legally appropriate", "Medication reviews and monitoring", "Ongoing treatment support", "Collaboration with specialist clinicians"] },
  { icon: Sparkles, color: "#10B981", title: "Skin & Dermatology",
    tagline: "Assessment and treatment of common skin conditions.",
    items: ["Acne", "Rosacea", "Eczema", "Psoriasis", "Hair and scalp concerns", "Minor skin condition assessments", "Dermatology referral pathways"] },
  { icon: ShieldPlus, color: "#06B6D4", title: "Sexual Health",
    tagline: "Discreet sexual wellbeing consultations and prescribing.",
    items: ["Erectile dysfunction", "Contraception", "STI screening and referral", "Sexual wellbeing consultations", "Appropriate prescribing services"] },
  { icon: HeartPulse, color: "#EF4444", title: "Cardiovascular Health",
    tagline: "Blood pressure, cholesterol and cardiovascular risk care.",
    items: ["Hypertension", "Cholesterol management", "Cardiovascular risk assessment", "Blood pressure monitoring", "Lifestyle and preventative care"] },
  { icon: Droplets, color: "#F59E0B", title: "Diabetes & Metabolic Health",
    tagline: "Type 2 diabetes reviews and metabolic risk support.",
    items: ["Type 2 diabetes reviews", "HbA1c screening pathways", "Prediabetes support", "Metabolic risk assessment", "Weight and lifestyle management"] },
  { icon: Salad, color: "#10B981", title: "Digestive Health",
    tagline: "Digestive health consultations with referral pathways.",
    items: ["Acid reflux", "Irritable bowel syndrome assessment", "Constipation", "Digestive health consultations", "Appropriate investigation and referral pathways"] },
  { icon: Plane, color: "#06B6D4", title: "Travel Health",
    tagline: "Travel consultations, vaccinations and destination advice.",
    items: ["Travel health consultations", "Travel vaccinations", "Malaria prevention", "Travel medication advice", "Destination-specific health advice"] },
  { icon: ClipboardList, color: "#EF4444", title: "General Private Healthcare",
    tagline: "Everyday private consultations, prescriptions and health checks.",
    items: ["Minor illness consultations", "Private prescriptions", "Medication reviews", "Smoking cessation", "Allergy consultations", "Health screening", "Blood testing", "Vitamin deficiency assessments", "Preventative health checks"] },
  { icon: Telescope, color: "#6366F1", title: "Specialist Services — Future Development",
    tagline: "Planned longer-term service development, introduced as governance allows.",
    items: ["GP-led consultations", "Specialist pharmacist clinics", "Sleep health", "Respiratory health", "Mental health assessment and referral", "Long-term condition management", "Selected aesthetic services, subject to additional training and governance"] },
];

const PHARMACY_BENEFITS = [
  { icon: Building2, title: "Make Use of Existing Space", text: "Explore using an appropriate consultation room within your existing pharmacy." },
  { icon: Stethoscope, title: "Expand Private Clinical Services", text: "Introduce selected private healthcare services appropriate to your premises, clinicians and regulatory arrangements." },
  { icon: BarChart3, title: "Develop New Revenue Opportunities", text: "Create opportunities for additional private consultation and prescribing income." },
  { icon: Sparkles, title: "NCS Clinic Branding", text: "Operate under a consistent and professionally developed clinic identity through an agreed licensing or partnership model." },
  { icon: GraduationCap, title: "Clinical Training & Support", text: "Access planned service-specific training, protocols and professional development." },
  { icon: Settings2, title: "Ongoing Operational Guidance", text: "Receive planned support with service setup, clinical workflows, marketing and operational delivery." },
];

const PRESCRIBER_BENEFITS = [
  "Use of the NCS Clinic brand, subject to an agreement",
  "Planned training and clinical service frameworks",
  "Support with clinic setup and service development",
  "Access to proposed GP and specialist pharmacist advisory pathways",
  "Marketing and patient acquisition support",
  "Future access to the NCS Clinic digital platform",
];

const SUPPORT_NODES = [
  { icon: FileSignature, label: "Independent Prescribing Pharmacists" },
  { icon: Stethoscope, label: "GP Clinical Advisors" },
  { icon: Pill, label: "Specialist Pharmacists" },
  { icon: ShieldCheck, label: "Clinical Governance & Quality" },
  { icon: GraduationCap, label: "Training & Professional Development" },
  { icon: MonitorSmartphone, label: "Digital & Operational Support" },
];

const STEPS = [
  { icon: ClipboardList, title: "Register Your Interest",
    text: "Tell us whether you are a community pharmacy owner or an independent prescribing pharmacist." },
  { icon: MessageSquare, title: "Initial Discussion",
    text: "Our team will discuss your location, experience, facilities, ambitions and proposed services." },
  { icon: Search, title: "Partnership Assessment",
    text: "Explore suitability, clinical competencies, premises requirements, regulatory considerations and the proposed commercial model." },
  { icon: GraduationCap, title: "Training & Clinic Development",
    text: "Subject to the programme launching and an agreement being signed, complete the required preparation, training, governance and operational setup." },
  { icon: Rocket, title: "Launch Your Clinic",
    text: "Following all necessary approvals, begin providing authorised NCS Clinic services with the agreed level of support." },
];

const PLATFORM_FEATURES = [
  { icon: CalendarCheck, title: "Online Appointments", text: "Patients could book suitable in-person and remote consultations." },
  { icon: Video, title: "Virtual Consultations", text: "Eligible clinicians could provide remote consultations where clinically appropriate and permitted." },
  { icon: UserCircle, title: "Patient Accounts", text: "Secure access to appointment information and relevant healthcare communications." },
  { icon: Pill, title: "Prescribing & Pharmacy Integration", text: "Potential electronic prescribing and dispensing workflows, subject to legal, technical and professional requirements." },
  { icon: MapPin, title: "Clinic Finder", text: "Help patients discover participating NCS Clinic locations." },
  { icon: HeartPulse, title: "Ongoing Treatment Support", text: "Follow-up appointments, monitoring reminders and relevant patient communications." },
  { icon: BarChart3, title: "Partner Dashboard", text: "Allow participating clinics to manage appointments and approved operational information." },
  { icon: Settings2, title: "Central NCS Administration", text: "Manage clinic partnerships, service configuration and appropriate performance reporting." },
];

const MAP_NODES = [
  { name: "Edinburgh", x: 58, y: 8 },
  { name: "Glasgow", x: 44, y: 14 },
  { name: "Belfast", x: 22, y: 24 },
  { name: "Newcastle", x: 58, y: 28 },
  { name: "Manchester", x: 50, y: 40 },
  { name: "Leeds", x: 60, y: 38 },
  { name: "Birmingham", x: 50, y: 56 },
  { name: "Cardiff", x: 36, y: 66 },
  { name: "London", x: 62, y: 74 },
];

const PILLARS = [
  { icon: Network, color: "#10B981", title: "National Clinic Network" },
  { icon: Stethoscope, color: "#06B6D4", title: "Clinician-Led Healthcare" },
  { icon: MonitorSmartphone, color: "#F59E0B", title: "Digital Healthcare Integration" },
  { icon: ShieldCheck, color: "#EF4444", title: "Quality & Clinical Governance" },
];

const REGULATORY = [
  "GPhC professional registration and scope of practice",
  "CQC registration requirements where applicable",
  "Clinical governance and safeguarding",
  "Prescribing protocols and prescribing accountability",
  "Medical indemnity and appropriate insurance",
  "Patient consent and confidentiality",
  "UK GDPR and health information security",
  "Relevant pharmacy and healthcare service regulations",
  "Specialist clinical oversight where required",
  "Appropriate arrangements for controlled drugs, referrals and follow-up",
];

const APPLICANT_TYPES = [
  "Community Pharmacy Owner",
  "Pharmacy Group",
  "Pharmacist Independent Prescriber",
  "GP / Medical Professional",
  "Healthcare Investor or Business Partner",
  "Other",
];
const PHARMACIST_TYPES = ["Pharmacist Independent Prescriber", "GP / Medical Professional"];
const PHARMACY_TYPES = ["Community Pharmacy Owner", "Pharmacy Group"];

const INTERESTS = [
  "Open an NCS Clinic Within My Pharmacy",
  "Open My Own NCS-Branded Clinic",
  "Become a Founding Clinic Partner",
  "Access Clinical Training",
  "Explore the NCS Clinic Brand Licence",
  "Provide GP or Specialist Clinical Support",
  "Discuss a Multi-Site Partnership",
  "Receive Information About the Online Platform",
];

const TIMESCALES = ["As soon as possible", "0–3 months", "3–6 months", "6–12 months", "12+ months", "Just exploring"];

/* --------------------------- Service card --------------------------- */
const ServiceCard = ({ s, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`clinic-service-card-${index + 1}`}
      className="h-full flex flex-col bg-white border border-[#E2E8F0] rounded-3xl p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
      <span className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${s.color}1A`, color: s.color }}>
        <s.icon size={20} />
      </span>
      <h3 className="mt-5 font-display text-base font-bold text-[#0B192C] leading-snug">{s.title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-[#475569]">{s.tagline}</p>
      {open && (
        <ul className="mt-4 space-y-1.5 border-t border-[#E2E8F0] pt-4">
          {s.items.map((it) => (
            <li key={it} className="flex items-start gap-2 text-xs leading-relaxed text-[#475569]">
              <Check size={13} className="shrink-0 mt-0.5" style={{ color: s.color }} /> {it}
            </li>
          ))}
        </ul>
      )}
      <button onClick={() => setOpen((v) => !v)} data-testid={`clinic-service-toggle-${index + 1}`}
        className="group mt-auto pt-5 inline-flex items-center gap-2 text-sm font-bold transition-colors duration-300"
        style={{ color: s.color }}
        aria-expanded={open}>
        {open ? "Show less" : "Learn more"}
        {open ? <Minus size={14} /> : <Plus size={14} />}
      </button>
    </div>
  );
};

/* --------------------------- Registration form --------------------------- */
const ClinicForm = ({ applicantType }) => {
  const empty = {
    applicant_type: applicantType || APPLICANT_TYPES[0],
    full_name: "", organisation: "", job_title: "", email: "", phone: "",
    postcode: "", city: "", region: "",
    gphc: "", ip_qualification: "Yes", clinical_areas: "", experience: "3–5 years", premises_access: "Exploring",
    branches: "", consultation_room: "Yes", ip_available: "No", services_interest: "", clinic_timescale: "Just exploring",
    interests: [], launch_timescale: "Just exploring", comments: "",
    consent_contact: false, consent_marketing: false, website: "",
  };
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleInterest = (c) => setForm((f) => ({
    ...f, interests: f.interests.includes(c) ? f.interests.filter((x) => x !== c) : [...f.interests, c],
  }));

  const isPharmacist = PHARMACIST_TYPES.includes(form.applicant_type);
  const isPharmacy = PHARMACY_TYPES.includes(form.applicant_type);
  const isIpPharmacist = form.applicant_type === "Pharmacist Independent Prescriber";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_contact) {
      toast.error("Please consent to being contacted about your enquiry.");
      return;
    }
    setSending(true);
    const details = [
      { label: "Applicant type", value: form.applicant_type },
      { label: "Area", value: `${form.city}, ${form.region} (${form.postcode})` },
      ...(form.job_title ? [{ label: "Job title", value: form.job_title }] : []),
      ...(isIpPharmacist && form.gphc ? [{ label: "GPhC registration", value: form.gphc }] : []),
      ...(isPharmacist ? [
        { label: "Independent prescribing qualification", value: form.ip_qualification },
        ...(form.clinical_areas ? [{ label: "Clinical areas of interest", value: form.clinical_areas }] : []),
        { label: "Years of experience", value: form.experience },
        { label: "Access to suitable premises", value: form.premises_access },
      ] : []),
      ...(isPharmacy ? [
        ...(form.branches ? [{ label: "Pharmacy branches", value: form.branches }] : []),
        { label: "Existing consultation room", value: form.consultation_room },
        { label: "Independent prescriber available", value: form.ip_available },
        ...(form.services_interest ? [{ label: "Services of interest", value: form.services_interest }] : []),
        { label: "Clinic timescale", value: form.clinic_timescale },
      ] : []),
      ...(form.interests.length ? [{ label: "Partnership interests", value: form.interests.join("; ") }] : []),
      { label: "Preferred launch timescale", value: form.launch_timescale },
      { label: "Marketing updates opt-in", value: form.consent_marketing ? "Yes" : "No" },
    ];
    try {
      await axios.post(`${API}/enquiries`, {
        full_name: form.full_name,
        organisation: form.organisation || `Individual — ${form.applicant_type}`,
        org_type: "NCS Clinic — Expression of Interest",
        email: form.email,
        phone: form.phone,
        requirement: `NCS Clinic — ${form.applicant_type}`,
        start_date: null,
        additional: form.comments || null,
        journey: "ncs-clinic",
        website: form.website,
        calculator: details,
      });
      setSent(true);
      toast.success("Thank you — your interest in NCS Clinic has been registered.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="cl-register-success" className="bg-white border border-[#10B981]/30 rounded-3xl p-10 sm:p-14 text-center shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
        <span className="mx-auto w-16 h-16 rounded-full bg-[#10B981]/10 text-[#10B981] flex items-center justify-center">
          <CheckCircle2 size={30} />
        </span>
        <h3 className="mt-6 font-display text-2xl font-bold text-[#0B192C]">Interest registered</h3>
        <p className="mt-3 text-sm sm:text-base text-[#475569] max-w-md mx-auto leading-relaxed">
          Thank you, {form.full_name.split(" ")[0]}. Your expression of interest is with our clinic
          partnerships team — as the NCS Clinic programme develops, we'll be in touch about next steps.
          A confirmation email is on its way to {form.email}.
        </p>
        <button data-testid="cl-register-new-button" onClick={() => { setSent(false); setForm({ ...empty }); }}
          className="mt-8 text-sm font-bold text-[#0B192C] border-b-2 border-[#10B981] pb-1 hover:text-[#10B981] transition-colors">
          Register another interest
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="cl-register-form"
      className="bg-white border border-[#E2E8F0] rounded-3xl p-8 sm:p-10 shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="sm:col-span-2">
          <label htmlFor="cl-type" className={labelCls}>Applicant type *</label>
          <select id="cl-type" data-testid="cl-select-applicant-type" value={form.applicant_type} onChange={upd("applicant_type")} className={inputCls}>
            {APPLICANT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      <p className="mt-8 font-display text-sm font-extrabold uppercase tracking-[0.16em] text-[#0F382C]">Contact details</p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="cl-name" className={labelCls}>Full name *</label>
          <input id="cl-name" data-testid="cl-input-name" required minLength={2}
            value={form.full_name} onChange={upd("full_name")} placeholder="Your full name" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cl-org" className={labelCls}>Organisation / pharmacy name</label>
          <input id="cl-org" data-testid="cl-input-organisation"
            value={form.organisation} onChange={upd("organisation")} placeholder="Riverside Pharmacy" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cl-job" className={labelCls}>Job title</label>
          <input id="cl-job" data-testid="cl-input-job-title"
            value={form.job_title} onChange={upd("job_title")} placeholder="Owner / Superintendent / IP Pharmacist" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cl-email" className={labelCls}>Email address *</label>
          <input id="cl-email" data-testid="cl-input-email" type="email" required
            value={form.email} onChange={upd("email")} placeholder="you@example.com" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cl-phone" className={labelCls}>Telephone number *</label>
          <input id="cl-phone" data-testid="cl-input-phone" type="tel" required minLength={5}
            value={form.phone} onChange={upd("phone")} placeholder="07123 456789" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cl-postcode" className={labelCls}>Postcode *</label>
          <input id="cl-postcode" data-testid="cl-input-postcode" required minLength={4}
            value={form.postcode} onChange={upd("postcode")} placeholder="WN1 1AA" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cl-city" className={labelCls}>Town / city *</label>
          <input id="cl-city" data-testid="cl-input-city" required minLength={2}
            value={form.city} onChange={upd("city")} placeholder="Wigan" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cl-region" className={labelCls}>Region *</label>
          <input id="cl-region" data-testid="cl-input-region" required minLength={2}
            value={form.region} onChange={upd("region")} placeholder="Greater Manchester" className={inputCls} />
        </div>
      </div>

      {isPharmacist && (
        <div data-testid="cl-pharmacist-fields">
          <p className="mt-8 font-display text-sm font-extrabold uppercase tracking-[0.16em] text-[#0F382C]">Professional details</p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {isIpPharmacist && (
              <div>
                <label htmlFor="cl-gphc" className={labelCls}>GPhC registration number</label>
                <input id="cl-gphc" data-testid="cl-input-gphc"
                  value={form.gphc} onChange={upd("gphc")} placeholder="e.g. 2211223" className={inputCls} />
              </div>
            )}
            <div>
              <label htmlFor="cl-ipq" className={labelCls}>Independent prescribing qualification</label>
              <select id="cl-ipq" data-testid="cl-select-ip-qualification" value={form.ip_qualification} onChange={upd("ip_qualification")} className={inputCls}>
                {["Yes", "No"].map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="cl-exp" className={labelCls}>Years of professional experience</label>
              <select id="cl-exp" data-testid="cl-select-experience" value={form.experience} onChange={upd("experience")} className={inputCls}>
                {["0–2 years", "3–5 years", "6–10 years", "10+ years"].map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="cl-premises" className={labelCls}>Access to suitable premises</label>
              <select id="cl-premises" data-testid="cl-select-premises" value={form.premises_access} onChange={upd("premises_access")} className={inputCls}>
                {["Yes", "No", "Exploring"].map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="cl-areas" className={labelCls}>Main clinical areas of interest</label>
              <input id="cl-areas" data-testid="cl-input-clinical-areas"
                value={form.clinical_areas} onChange={upd("clinical_areas")} placeholder="e.g. weight management, men's health, travel health" className={inputCls} />
            </div>
          </div>
        </div>
      )}

      {isPharmacy && (
        <div data-testid="cl-pharmacy-fields">
          <p className="mt-8 font-display text-sm font-extrabold uppercase tracking-[0.16em] text-[#0F382C]">Pharmacy details</p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="cl-branches" className={labelCls}>Number of pharmacy branches</label>
              <input id="cl-branches" data-testid="cl-input-branches" type="number" min="1"
                value={form.branches} onChange={upd("branches")} placeholder="e.g. 3" className={inputCls} />
            </div>
            <div>
              <label htmlFor="cl-consult" className={labelCls}>Existing consultation room</label>
              <select id="cl-consult" data-testid="cl-select-consultation-room" value={form.consultation_room} onChange={upd("consultation_room")} className={inputCls}>
                {["Yes", "No"].map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="cl-ipavail" className={labelCls}>Independent prescriber already available</label>
              <select id="cl-ipavail" data-testid="cl-select-ip-available" value={form.ip_available} onChange={upd("ip_available")} className={inputCls}>
                {["Yes", "No"].map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="cl-clinictime" className={labelCls}>Approximate timescale for introducing a clinic</label>
              <select id="cl-clinictime" data-testid="cl-select-clinic-timescale" value={form.clinic_timescale} onChange={upd("clinic_timescale")} className={inputCls}>
                {TIMESCALES.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="cl-services" className={labelCls}>Proposed services of interest</label>
              <input id="cl-services" data-testid="cl-input-services-interest"
                value={form.services_interest} onChange={upd("services_interest")} placeholder="e.g. weight management, travel health, men's health" className={inputCls} />
            </div>
          </div>
        </div>
      )}

      <p className="mt-8 font-display text-sm font-extrabold uppercase tracking-[0.16em] text-[#0F382C]">Partnership interests</p>
      <div className="mt-4 flex flex-wrap gap-2.5" data-testid="cl-interests">
        {INTERESTS.map((c) => {
          const active = form.interests.includes(c);
          const key = c.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40);
          return (
            <button type="button" key={c} data-testid={`cl-interest-${key}`}
              onClick={() => toggleInterest(c)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-bold border transition-colors duration-300 ${
                active ? "bg-[#0B192C] text-white border-[#0B192C]" : "bg-white text-[#475569] border-[#E2E8F0] hover:border-[#0B192C]"
              }`}>
              {active && <Check size={13} />} {c}
            </button>
          );
        })}
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="cl-launch" className={labelCls}>Preferred launch timescale</label>
          <select id="cl-launch" data-testid="cl-select-launch-timescale" value={form.launch_timescale} onChange={upd("launch_timescale")} className={inputCls}>
            {TIMESCALES.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="cl-comments" className={labelCls}>Additional comments</label>
          <textarea id="cl-comments" data-testid="cl-input-comments" rows={4}
            value={form.comments} onChange={upd("comments")}
            placeholder="Your ambitions, premises details, questions about the model..."
            className={`${inputCls} resize-none`} />
        </div>
      </div>

      <div className="mt-6 space-y-3">
        <label htmlFor="cl-consent-contact" className="flex items-start gap-3 cursor-pointer select-none">
          <input id="cl-consent-contact" type="checkbox" data-testid="cl-consent-contact"
            checked={form.consent_contact} onChange={(e) => setForm((f) => ({ ...f, consent_contact: e.target.checked }))}
            className="mt-0.5 w-[18px] h-[18px] accent-[#10B981]" />
          <span className="text-sm text-[#475569]">
            I consent to NCS contacting me about this enquiry. *
          </span>
        </label>
        <label htmlFor="cl-consent-marketing" className="flex items-start gap-3 cursor-pointer select-none">
          <input id="cl-consent-marketing" type="checkbox" data-testid="cl-consent-marketing"
            checked={form.consent_marketing} onChange={(e) => setForm((f) => ({ ...f, consent_marketing: e.target.checked }))}
            className="mt-0.5 w-[18px] h-[18px] accent-[#10B981]" />
          <span className="text-sm text-[#475569]">
            I'd also like to receive future updates about the NCS Clinic programme (optional).
          </span>
        </label>
      </div>

      <div className="hidden" aria-hidden="true">
        <label htmlFor="cl-web">Website</label>
        <input id="cl-web" data-testid="cl-honeypot" tabIndex={-1} autoComplete="off"
          value={form.website} onChange={upd("website")} />
      </div>

      <div className="mt-6 flex items-start gap-3 bg-[#F1F5F9] rounded-2xl px-5 py-4">
        <ShieldCheck size={17} className="text-[#10B981] shrink-0 mt-0.5" />
        <p className="text-xs leading-relaxed text-[#475569]">
          Your information is stored securely, never displayed publicly, and used only to progress your
          enquiry. NCS Clinic is a development project — registering interest creates no obligation on
          either side. Read our <PrivacyLink testid="clinic-privacy-link" />.
        </p>
      </div>

      <button type="submit" disabled={sending} data-testid="cl-submit"
        className="group btn-shine mt-7 inline-flex items-center justify-center gap-2.5 w-full sm:w-auto bg-[#0B192C] text-white font-bold px-8 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#0F382C] disabled:opacity-60 disabled:cursor-not-allowed">
        {sending ? (<>Submitting <Loader2 size={16} className="animate-spin" /></>)
          : (<>Register My Interest <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" /></>)}
      </button>
    </form>
  );
};

/* --------------------------------- Page --------------------------------- */
const NcsClinic = () => {
  const content = useSiteContent();
  const [preselect, setPreselect] = useState(null);

  const openForm = (type) => {
    setPreselect(type + "#" + Date.now());
    scrollToId("register");
  };
  const preselectValue = preselect ? preselect.split("#")[0] : null;

  return (
    <main data-testid="clinic-page">
      <Seo
        title="NCS Clinic | Pharmacist-Led Private Clinics — Register Your Interest"
        description="NCS Clinic is a developing UK-wide network of private, pharmacist-led healthcare clinics. Community pharmacy owners and independent prescribing pharmacists can register interest as founding partners."
      />

      {/* 1 — HERO */}
      <section className="relative overflow-hidden pt-32 sm:pt-40 pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="inline-flex items-center gap-2" data-testid="clinic-hero-badge">
              <span className="inline-flex items-center gap-2 bg-[#F59E0B]/15 text-[#B45309] text-[11px] font-extrabold uppercase tracking-[0.16em] rounded-full px-4 py-2">
                <Rocket size={13} /> Coming Soon — Register Your Interest
              </span>
            </p>
            <h1 data-testid="clinic-hero-heading"
              className="mt-5 font-display font-extrabold tracking-tight leading-[1.06] text-[#0B192C] text-4xl sm:text-5xl lg:text-6xl">
              <MaskedLine delay={0.15}>NCS Clinic</MaskedLine>
              <MaskedLine delay={0.3}>
                <span className="font-serif-accent">Your Clinic. Our Expertise.</span> One National Vision.
              </MaskedLine>
            </h1>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.55 }}
              className="mt-6 space-y-4 text-base leading-relaxed text-[#475569] max-w-2xl">
              <p className="font-semibold text-[#0B192C]">Introducing the next chapter of National Clinical Services.</p>
              <p>
                NCS Clinic is being developed as a nationwide network of private, pharmacist-led
                healthcare clinics, bringing together independent prescribing, specialist clinical
                support and modern digital healthcare.
              </p>
              <p>
                Whether you are a community pharmacy looking to expand into private clinical services
                or an independent prescribing pharmacist with ambitions to establish your own clinic,
                NCS Clinic aims to provide the brand, training, clinical frameworks and ongoing support
                to help you build your service. Our ambition is to make private healthcare more
                accessible while creating new opportunities for pharmacy professionals across the UK.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7 }}
              className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
              {HIGHLIGHTS.map((hl, i) => (
                <span key={hl.label} data-testid={`clinic-highlight-${i + 1}`}
                  className="inline-flex items-center gap-2.5 bg-white border border-[#E2E8F0] rounded-full px-4 py-3">
                  <hl.icon size={16} style={{ color: hl.color }} />
                  <span className="text-xs sm:text-sm font-semibold text-[#0B192C]">{hl.label}</span>
                </span>
              ))}
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.82 }}
              className="mt-8 flex flex-wrap gap-4">
              <button onClick={() => scrollToId("register")} data-testid="clinic-hero-partner-btn"
                className="group inline-flex items-center gap-2.5 bg-[#C5A059] text-[#0B192C] font-bold px-7 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#0B192C] hover:text-white">
                Become a Founding Clinic Partner
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button onClick={() => scrollToId("services")} data-testid="clinic-hero-model-btn"
                className="inline-flex items-center gap-2.5 text-[#0B192C] font-semibold px-7 py-4 rounded-full text-sm border border-[#0B192C]/25 transition-colors duration-300 hover:bg-[#0B192C] hover:text-white">
                Explore Our Clinic Model
              </button>
            </motion.div>
          </div>
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative">
              <img src="/assets/ncs-mark.png?v=2" alt="" aria-hidden="true"
                className="absolute -top-12 -left-12 w-40 opacity-20 -rotate-12 select-none pointer-events-none" />
              <div className="relative rounded-3xl overflow-hidden border-[6px] border-white shadow-[0_16px_40px_-12px_rgba(11,25,44,0.12)]">
                <SiteImg content={content} slot="clinic_hero" fallback="/assets/clinic-hero.png" testid="clinic-hero-image"
                  alt="A pharmacist independent prescriber consulting a patient in a premium private clinic room"
                  className="w-full h-[340px] sm:h-[440px] object-cover" />
              </div>
              <div className="absolute -bottom-6 left-6 bg-white rounded-2xl shadow-sm border border-[#E2E8F0] px-5 py-3.5">
                <p className="font-display text-sm font-bold text-[#0B192C]">Private healthcare, pharmacist-led.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2 — SERVICES */}
      <section id="services" className="py-20 sm:py-28 lg:py-32 bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">Our private healthcare services</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C] max-w-3xl">
              One Clinic. A Comprehensive Range of Private Healthcare Services.
            </h2>
            <p className="mt-4 text-base text-[#475569] max-w-2xl">
              Our proposed clinic model brings together private consultations, prescribing services,
              preventative healthcare and specialist treatment pathways.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.06}>
                <ServiceCard s={s} index={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 3 — FOR COMMUNITY PHARMACIES */}
      <section id="pharmacy" className="py-20 sm:py-28 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">Bring NCS Clinic to your community pharmacy</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C]">
              Transform Your Pharmacy Into a Private Healthcare Destination
            </h2>
            <p className="mt-3 text-lg font-semibold text-[#0B192C]">Your existing pharmacy. New clinical opportunities.</p>
            <div className="mt-5 space-y-4 text-base leading-relaxed text-[#475569]">
              <p>
                Community pharmacies are ideally positioned to expand access to private healthcare.
                NCS Clinic is developing a partnership model designed to help eligible pharmacies
                introduce additional clinical services using suitable existing consultation facilities,
                where possible.
              </p>
              <p>
                Rather than building a completely new healthcare business from scratch, participating
                pharmacies could benefit from an established central brand, planned clinical training,
                operational guidance and ongoing support.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden border border-[#E2E8F0] shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
              <SiteImg content={content} slot="clinic_pharmacy" fallback="/assets/clinic-pharmacy.png" testid="clinic-pharmacy-image"
                alt="A premium consultation room inside a modern community pharmacy"
                className="w-full h-[280px] sm:h-[360px] object-cover" />
            </div>
          </Reveal>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PHARMACY_BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={(i % 3) * 0.08}>
                <div data-testid={`clinic-benefit-card-${i + 1}`}
                  className="h-full bg-white border border-[#E2E8F0] rounded-3xl p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
                  <span className="w-11 h-11 rounded-2xl bg-[#0B192C] text-[#10B981] flex items-center justify-center">
                    <b.icon size={20} />
                  </span>
                  <h3 className="mt-5 font-display text-base font-bold text-[#0B192C] leading-snug">{b.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-[#475569]">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10">
            <div className="bg-[#0B192C] rounded-3xl px-8 py-8 sm:px-10 flex flex-col lg:flex-row lg:items-center gap-6">
              <div className="flex-1">
                <p className="font-display text-lg sm:text-xl font-bold text-white">
                  A clinic model designed to work with your existing pharmacy infrastructure.
                </p>
                <p className="mt-2 text-sm leading-relaxed text-white/60">
                  Initial premises investment may be relatively modest where appropriate consultation
                  facilities already exist — but actual setup costs depend on premises, equipment,
                  staffing, governance, insurance, regulatory requirements and the selected services.
                </p>
              </div>
              <button onClick={() => openForm("Community Pharmacy Owner")} data-testid="clinic-pharmacy-cta"
                className="group shrink-0 inline-flex items-center gap-2.5 bg-[#C5A059] text-[#0B192C] font-bold px-7 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-white">
                Register Your Pharmacy as a Potential NCS Clinic Partner
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 4 — FOR INDEPENDENT PRESCRIBERS */}
      <section id="prescribers" className="py-20 sm:py-28 lg:py-32 bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5 order-2 lg:order-1">
            <div className="rounded-3xl overflow-hidden border border-[#E2E8F0] shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
              <SiteImg content={content} slot="clinic_prescriber" fallback="/assets/clinic-prescriber.png" testid="clinic-prescriber-image"
                alt="A pharmacist independent prescriber discussing a treatment plan with a patient in a private clinic"
                className="w-full h-[280px] sm:h-[360px] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7 order-1 lg:order-2">
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">For independent prescribing pharmacists</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C]">
              You're an Independent Prescriber. Now Build Something of Your Own.
            </h2>
            <div className="mt-5 space-y-4 text-base leading-relaxed text-[#475569]">
              <p>
                You have developed your clinical expertise. Now imagine building your own private
                healthcare service with the support of a wider professional network.
              </p>
              <p>
                NCS Clinic is being designed to help eligible pharmacist independent prescribers explore
                establishing their own branded clinics. Whether you already have access to suitable
                premises or want to collaborate with a community pharmacy, our proposed model aims to
                make starting a private clinical service more structured and accessible.
              </p>
            </div>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {PRESCRIBER_BENEFITS.map((b, i) => (
                <li key={b} data-testid={`clinic-prescriber-benefit-${i + 1}`} className="flex items-start gap-2.5 text-sm text-[#0B192C]">
                  <Check size={15} className="text-[#10B981] shrink-0 mt-0.5" /> {b}
                </li>
              ))}
            </ul>
            <p className="mt-7 font-display text-xl sm:text-2xl font-bold text-[#0B192C]">
              Your clinical skills. Your business ambition. <span className="font-serif-accent text-[#06B6D4]">Our shared network.</span>
            </p>
            <button onClick={() => openForm("Pharmacist Independent Prescriber")} data-testid="clinic-prescriber-cta"
              className="group mt-6 inline-flex items-center gap-2.5 bg-[#0B192C] text-white font-bold px-7 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#0F382C] hover:text-[#0B192C]">
              Explore Becoming an NCS Clinic Partner
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Reveal>
        </div>
      </section>

      {/* 5 — CLINICAL SUPPORT MODEL */}
      <section id="support" className="py-20 sm:py-28 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center">
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">Our clinical support model</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C] max-w-2xl mx-auto">
              Independent Clinics. Connected Clinical Expertise.
            </h2>
            <p className="mt-4 text-base text-[#475569] max-w-2xl mx-auto">
              NCS Clinic aims to develop a multidisciplinary clinical support framework bringing
              together appropriately qualified independent prescribing pharmacists, GPs and specialist
              pharmacists — focused on clinical safety, professional development, appropriate referral
              pathways and ongoing service quality.
            </p>
          </Reveal>

          {/* Desktop radial diagram */}
          <Reveal className="mt-14 hidden lg:block">
            <div className="relative h-[560px]" data-testid="clinic-support-diagram">
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1000 560" fill="none" aria-hidden="true">
                {SUPPORT_NODES.map((_, i) => {
                  const angle = (i * 60 - 90) * (Math.PI / 180);
                  const x = 500 + 340 * Math.cos(angle);
                  const y = 280 + 205 * Math.sin(angle);
                  return <line key={i} x1="500" y1="280" x2={x} y2={y} stroke="#06B6D4" strokeOpacity="0.35" strokeWidth="2" strokeDasharray="6 6" />;
                })}
              </svg>
              <div data-testid="clinic-support-hub"
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-44 h-44 rounded-full bg-[#0B192C] flex flex-col items-center justify-center text-center shadow-[0_16px_40px_-12px_rgba(11,25,44,0.3)]">
                <img src="/assets/ncs-mark.png" alt="" className="w-10 h-10" />
                <p className="mt-2 font-display text-sm font-extrabold text-white leading-tight">NCS<br />Clinic</p>
              </div>
              {SUPPORT_NODES.map((n, i) => {
                const angle = (i * 60 - 90) * (Math.PI / 180);
                const x = 50 + 34 * Math.cos(angle);
                const y = 50 + 36.6 * Math.sin(angle);
                return (
                  <div key={n.label} data-testid={`clinic-support-node-${i + 1}`}
                    className="absolute -translate-x-1/2 -translate-y-1/2 w-52 bg-white border border-[#E2E8F0] rounded-2xl px-4 py-3.5 flex items-center gap-3 shadow-[0_10px_30px_rgba(11,25,44,0.04)]"
                    style={{ left: `${x}%`, top: `${y}%` }}>
                    <span className="w-9 h-9 rounded-xl bg-[#06B6D4]/12 text-[#06B6D4] flex items-center justify-center shrink-0">
                      <n.icon size={17} />
                    </span>
                    <p className="text-xs font-bold text-[#0B192C] leading-snug">{n.label}</p>
                  </div>
                );
              })}
            </div>
          </Reveal>
          {/* Mobile/stacked fallback */}
          <div className="mt-10 lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-4">
            {SUPPORT_NODES.map((n, i) => (
              <div key={n.label} data-testid={`clinic-support-node-m-${i + 1}`}
                className="bg-white border border-[#E2E8F0] rounded-2xl px-4 py-3.5 flex items-center gap-3">
                <span className="w-9 h-9 rounded-xl bg-[#06B6D4]/12 text-[#06B6D4] flex items-center justify-center shrink-0">
                  <n.icon size={17} />
                </span>
                <p className="text-xs font-bold text-[#0B192C] leading-snug">{n.label}</p>
              </div>
            ))}
          </div>

          <Reveal className="mt-10">
            <div className="max-w-3xl mx-auto text-center space-y-3">
              <p className="text-sm leading-relaxed text-[#475569]">
                Our aim is that clinic partners will have access to defined professional support
                pathways rather than having to develop every clinical process independently.
              </p>
              <p className="text-xs leading-relaxed text-[#94A3B8]">
                Every clinic and clinician remains subject to appropriate professional
                responsibilities, clinical governance and regulatory requirements. Advisory
                arrangements are being developed; specific clinical appointments are confirmed only
                as the programme progresses.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 6 — PARTNERSHIP MODEL */}
      <section id="model" className="py-20 sm:py-28 lg:py-32 bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center">
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">The NCS Clinic partnership model</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C] max-w-3xl mx-auto">
              A Scalable Model for Community Pharmacies and Independent Prescribers
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-7">
            <Reveal>
              <div data-testid="clinic-model-pharmacy"
                className="h-full flex flex-col bg-[#F8FAFC] border border-[#E2E8F0] rounded-3xl p-8 sm:p-10">
                <span className="w-12 h-12 rounded-2xl bg-[#10B981]/12 text-[#10B981] flex items-center justify-center">
                  <Store size={22} />
                </span>
                <h3 className="mt-5 font-display text-xl font-bold text-[#0B192C]">Community Pharmacy Partner</h3>
                <p className="mt-4 text-[11px] uppercase tracking-[0.16em] font-extrabold text-[#64748B]">Ideal for</p>
                <ul className="mt-2 space-y-2">
                  {["Independent pharmacy owners", "Pharmacy groups", "Pharmacies with consultation rooms", "Businesses looking to expand private clinical services"].map((x) => (
                    <li key={x} className="flex items-start gap-2.5 text-sm text-[#475569]">
                      <Check size={15} className="text-[#10B981] shrink-0 mt-0.5" /> {x}
                    </li>
                  ))}
                </ul>
                <p className="mt-5 text-[11px] uppercase tracking-[0.16em] font-extrabold text-[#64748B]">Potential benefits</p>
                <ul className="mt-2 space-y-2">
                  {["NCS Clinic branding", "Training and service development", "Access to approved clinical pathways", "Operational and marketing support", "Future online booking integration", "Participation in a wider clinic network"].map((x) => (
                    <li key={x} className="flex items-start gap-2.5 text-sm text-[#0B192C] font-medium">
                      <Check size={15} className="text-[#10B981] shrink-0 mt-0.5" /> {x}
                    </li>
                  ))}
                </ul>
                <button onClick={() => openForm("Community Pharmacy Owner")} data-testid="clinic-model-pharmacy-cta"
                  className="group mt-8 inline-flex items-center justify-center gap-2.5 w-full bg-[#0B192C] text-white font-bold px-6 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#0F382C] hover:text-[#0B192C]">
                  Register Your Pharmacy
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div data-testid="clinic-model-prescriber"
                className="h-full flex flex-col bg-[#0B192C] rounded-3xl p-8 sm:p-10">
                <span className="w-12 h-12 rounded-2xl bg-[#06B6D4]/15 text-[#06B6D4] flex items-center justify-center">
                  <FileSignature size={22} />
                </span>
                <h3 className="mt-5 font-display text-xl font-bold text-white">Independent Prescriber Partner</h3>
                <p className="mt-4 text-[11px] uppercase tracking-[0.16em] font-extrabold text-white/50">Ideal for</p>
                <ul className="mt-2 space-y-2">
                  {["GPhC-registered pharmacist independent prescribers", "Pharmacists seeking to open a private clinic", "Clinicians interested in private healthcare entrepreneurship", "Prescribers seeking suitable pharmacy partnerships"].map((x) => (
                    <li key={x} className="flex items-start gap-2.5 text-sm text-white/70">
                      <Check size={15} className="text-[#06B6D4] shrink-0 mt-0.5" /> {x}
                    </li>
                  ))}
                </ul>
                <p className="mt-5 text-[11px] uppercase tracking-[0.16em] font-extrabold text-white/50">Potential benefits</p>
                <ul className="mt-2 space-y-2">
                  {["Branded clinic model", "Setup and operational guidance", "Clinical training and support pathways", "Marketing resources", "Future digital platform integration", "Professional networking opportunities"].map((x) => (
                    <li key={x} className="flex items-start gap-2.5 text-sm text-white font-medium">
                      <Check size={15} className="text-[#06B6D4] shrink-0 mt-0.5" /> {x}
                    </li>
                  ))}
                </ul>
                <button onClick={() => openForm("Pharmacist Independent Prescriber")} data-testid="clinic-model-prescriber-cta"
                  className="group mt-8 inline-flex items-center justify-center gap-2.5 w-full bg-[#C5A059] text-[#0B192C] font-bold px-6 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-white">
                  Register as an Independent Prescriber
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 7 — JOURNEY */}
      <section id="journey" className="py-20 sm:py-28 lg:py-32 bg-[#0B192C] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Reveal className="text-center">
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#C5A059]">How to join the NCS Clinic network</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">
              Your Journey to Becoming an NCS Clinic Partner
            </h2>
          </Reveal>
          <div className="mt-14 flex flex-col lg:flex-row items-stretch justify-center gap-4 lg:gap-2">
            {STEPS.map((s, i) => (
              <Fragment key={s.title}>
                <Reveal delay={i * 0.1} className="flex-1">
                  <div className="w-full h-full flex flex-col bg-white/[0.06] border border-white/10 rounded-3xl p-6 transition-colors duration-300 hover:bg-white/[0.10]"
                    data-testid={`clinic-step-${i + 1}`}>
                    <div className="flex items-center gap-3">
                      <span className="w-9 h-9 rounded-full bg-[#C5A059] text-[#0B192C] font-display font-extrabold text-sm flex items-center justify-center shrink-0">
                        {i + 1}
                      </span>
                      <span className="w-11 h-11 rounded-2xl bg-[#10B981]/15 text-[#10B981] flex items-center justify-center shrink-0">
                        <s.icon size={20} />
                      </span>
                    </div>
                    <h3 className="mt-4 font-display text-base font-bold text-white leading-snug uppercase tracking-wide">{s.title}</h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-white/60 flex-1">{s.text}</p>
                  </div>
                </Reveal>
                {i < STEPS.length - 1 && (
                  <>
                    <ArrowRight size={18} className="hidden lg:block text-[#10B981] self-center shrink-0" aria-hidden="true" />
                    <ArrowDown size={20} className="lg:hidden text-[#10B981] self-center shrink-0" aria-hidden="true" />
                  </>
                )}
              </Fragment>
            ))}
          </div>
          <Reveal className="text-center mt-10">
            <p className="text-xs leading-relaxed text-white/40 max-w-2xl mx-auto">
              This is the proposed partner onboarding pathway as the programme develops — not a
              guaranteed launch process or an offer of a franchise agreement.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 8 — ONLINE PLATFORM */}
      <section id="platform" className="py-20 sm:py-28 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <Reveal className="lg:col-span-6">
              <p className="inline-flex items-center gap-2">
                <span data-testid="clinic-platform-label"
                  className="inline-flex items-center gap-2 bg-[#06B6D4]/12 text-[#0E7490] text-[11px] font-extrabold uppercase tracking-[0.16em] rounded-full px-4 py-2">
                  <MonitorSmartphone size={13} /> In Development
                </span>
              </p>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C]">
                One Digital Platform. A Connected Network of Clinics.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-[#475569]">
                Alongside our physical clinic network, we aim to develop a dedicated NCS Clinic online
                platform connecting patients with suitable healthcare professionals and participating
                clinics. Our vision is to combine local in-person healthcare with the convenience of
                digital services.
              </p>
              <div className="mt-7 rounded-3xl overflow-hidden border border-[#E2E8F0] shadow-[0_10px_30px_rgba(11,25,44,0.04)] max-w-md">
                <SiteImg content={content} slot="clinic_online" fallback="/assets/clinic-online.png" testid="clinic-online-image"
                  alt="A pharmacist providing a remote video consultation on a laptop in a modern clinic office"
                  className="w-full h-[220px] sm:h-[260px] object-cover" />
              </div>
            </Reveal>
            <Reveal delay={0.12} className="lg:col-span-6">
              {/* CSS device mockup — proposed interface concept */}
              <div className="relative mx-auto max-w-lg" data-testid="clinic-platform-mockup" aria-hidden="true">
                <div className="bg-[#0B192C] rounded-[1.8rem] p-2.5 shadow-[0_24px_50px_-12px_rgba(11,25,44,0.25)]">
                  <div className="bg-[#F8FAFC] rounded-2xl overflow-hidden">
                    <div className="bg-white border-b border-[#E2E8F0] px-4 py-2.5 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-md bg-[#0B192C] flex items-center justify-center">
                        <span className="w-2 h-2 rounded-full bg-[#10B981]" />
                      </span>
                      <span className="text-[10px] font-extrabold tracking-wide text-[#0B192C]">NCS CLINIC</span>
                      <span className="ml-auto text-[8px] font-bold text-[#94A3B8] uppercase tracking-wider">Concept</span>
                    </div>
                    <div className="p-4 grid grid-cols-3 gap-3">
                      <div className="col-span-2 space-y-3">
                        <div className="bg-white rounded-xl border border-[#E2E8F0] p-3">
                          <p className="text-[9px] font-extrabold text-[#0B192C]">Good morning, Alex</p>
                          <p className="text-[8px] text-[#94A3B8] mt-0.5">Your next appointment is Thursday</p>
                          <div className="mt-2 h-6 rounded-lg bg-[#10B981] flex items-center justify-center">
                            <span className="text-[8px] font-bold text-[#0B192C]">Book a consultation</span>
                          </div>
                        </div>
                        <div className="bg-white rounded-xl border border-[#E2E8F0] p-3 flex items-center gap-2">
                          <span className="w-6 h-6 rounded-lg bg-[#06B6D4]/15" />
                          <div className="flex-1">
                            <div className="h-1.5 w-4/5 rounded bg-[#E2E8F0]" />
                            <div className="h-1.5 w-3/5 rounded bg-[#EEF2F7] mt-1.5" />
                          </div>
                        </div>
                        <div className="bg-white rounded-xl border border-[#E2E8F0] p-3 flex items-center gap-2">
                          <span className="w-6 h-6 rounded-lg bg-[#F59E0B]/15" />
                          <div className="flex-1">
                            <div className="h-1.5 w-3/4 rounded bg-[#E2E8F0]" />
                            <div className="h-1.5 w-1/2 rounded bg-[#EEF2F7] mt-1.5" />
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {["#10B981", "#06B6D4", "#F59E0B", "#EF4444"].map((c) => (
                          <div key={c} className="bg-white rounded-xl border border-[#E2E8F0] p-2.5">
                            <span className="block w-4 h-4 rounded-md" style={{ backgroundColor: `${c}26` }} />
                            <div className="h-1.5 w-full rounded bg-[#E2E8F0] mt-2" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-8 -right-4 sm:-right-8 w-24 sm:w-28 bg-[#0B192C] rounded-[1.4rem] p-1.5 shadow-[0_16px_36px_-10px_rgba(11,25,44,0.35)]">
                  <div className="bg-white rounded-[1rem] overflow-hidden">
                    <div className="bg-[#0B192C] px-2.5 py-2">
                      <div className="h-1.5 w-2/3 rounded bg-white/30 mx-auto" />
                    </div>
                    <div className="p-2.5 space-y-2">
                      <div className="rounded-lg bg-[#F0FDF4] border border-[#DCFCE7] p-2">
                        <div className="h-1.5 w-4/5 rounded bg-[#10B981]/40" />
                        <div className="h-1.5 w-3/5 rounded bg-[#E2E8F0] mt-1" />
                      </div>
                      <div className="rounded-lg border border-[#E2E8F0] p-2">
                        <div className="h-1.5 w-3/4 rounded bg-[#E2E8F0]" />
                        <div className="h-1.5 w-1/2 rounded bg-[#EEF2F7] mt-1" />
                      </div>
                      <div className="h-6 rounded-lg bg-[#0B192C]" />
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
          <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PLATFORM_FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={(i % 4) * 0.06}>
                <div data-testid={`clinic-platform-feature-${i + 1}`}
                  className="h-full bg-white border border-[#E2E8F0] rounded-3xl p-6">
                  <span className="w-10 h-10 rounded-xl bg-[#06B6D4]/12 text-[#06B6D4] flex items-center justify-center">
                    <f.icon size={18} />
                  </span>
                  <h3 className="mt-4 font-display text-sm font-bold text-[#0B192C] leading-snug">{f.title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-[#475569]">{f.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8">
            <p className="text-xs leading-relaxed text-[#94A3B8] max-w-3xl">
              The platform is planned, not yet available. Any future prescribing, consultation or
              records functionality will be built only after the required clinical safety, information
              governance, security and regulatory assessment.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 9 — NATIONAL VISION */}
      <section id="vision" className="py-20 sm:py-28 lg:py-32 bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">Our national vision</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C]">
              Our Vision: A Nationwide Network of NCS Clinics
            </h2>
            <p className="mt-3 text-lg font-semibold text-[#0B192C]">
              Building one of the UK's leading pharmacist-led private healthcare networks.
            </p>
            <div className="mt-5 space-y-4 text-base leading-relaxed text-[#475569]">
              <p>
                Our ambition is to create a nationally recognised private healthcare clinic network
                that empowers independent prescribing pharmacists, supports community pharmacy
                businesses and improves access to convenient clinical services.
              </p>
              <p>
                Through consistent branding, high-quality clinical frameworks, professional support
                and digital innovation, NCS Clinic aims to create a scalable model that can grow
                across the United Kingdom.
              </p>
            </div>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {PILLARS.map((p, i) => (
                <div key={p.title} data-testid={`clinic-pillar-${i + 1}`}
                  className="flex items-center gap-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl px-5 py-4">
                  <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${p.color}1A`, color: p.color }}>
                    <p.icon size={18} />
                  </span>
                  <p className="font-display text-sm font-bold text-[#0B192C] leading-snug">{p.title}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-6">
            <div data-testid="clinic-map"
              className="relative bg-[#0B192C] rounded-3xl p-6 sm:p-8 overflow-hidden">
              <div className="absolute inset-0 opacity-[0.35]"
                style={{ backgroundImage: "radial-gradient(rgba(6,182,212,0.25) 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
              <div className="relative mx-auto max-w-sm aspect-[4/5]">
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                  {MAP_NODES.slice(1).map((n, i) => {
                    const prev = MAP_NODES[i];
                    return <line key={n.name} x1={prev.x} y1={prev.y} x2={n.x} y2={n.y} stroke="#06B6D4" strokeOpacity="0.3" strokeWidth="0.4" strokeDasharray="1.5 1.5" />;
                  })}
                </svg>
                {MAP_NODES.map((n) => (
                  <div key={n.name} className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"
                    style={{ left: `${n.x}%`, top: `${n.y}%` }}>
                    <span className="relative flex w-3.5 h-3.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#10B981] opacity-40" style={{ animationDuration: "2.4s" }} />
                      <span className="relative inline-flex rounded-full w-3.5 h-3.5 bg-[#10B981] border-2 border-[#0B192C]" />
                    </span>
                    <span className="mt-1.5 text-[9px] font-bold text-white/70 whitespace-nowrap">{n.name}</span>
                  </div>
                ))}
              </div>
              <div className="relative mt-6 text-center">
                <p className="text-[11px] leading-relaxed text-white/40 max-w-xs mx-auto">
                  Expressions of interest only — no territories, fees, earnings or launch dates are promised at this stage.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 10 — FOUNDING PARTNERS */}
      <section className="py-20 sm:py-28 lg:py-32 bg-[#0B192C] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#C5A059]">Founding clinic partners</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">
              Be Part of the First Generation of NCS Clinics
            </h2>
            <p className="mt-5 text-base leading-relaxed text-white/65">
              We are inviting expressions of interest from community pharmacy owners and qualified
              independent prescribing pharmacists who would like to explore joining the NCS Clinic
              network during its development. Early interested partners will have the opportunity to
              discuss the proposed clinic model, service offerings, setup requirements and potential
              commercial arrangements as the programme develops.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <button onClick={() => scrollToId("register")} data-testid="clinic-founding-cta"
                className="group inline-flex items-center gap-2.5 bg-[#C5A059] text-[#0B192C] font-bold px-8 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-white">
                Register as a Potential Founding Partner
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <a href="/assets/ncs-clinic-prospectus.pdf" download="NCS-Clinic-Founding-Partner-Prospectus.pdf"
                data-testid="clinic-prospectus-btn"
                className="inline-flex items-center gap-2.5 text-white font-semibold px-7 py-4 rounded-full text-sm border border-white/35 transition-colors duration-300 hover:bg-white hover:text-[#0B192C]">
                <Download size={15} /> Download the Prospectus (PDF)
              </a>
            </div>
            <p className="mt-5 text-[11px] text-white/35">
              Expressions of interest only — no territories, fees, earnings or launch dates are promised at this stage.
            </p>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden border border-white/10 shadow-[0_16px_40px_-12px_rgba(0,0,0,0.4)]">
              <SiteImg content={content} slot="clinic_partners" fallback="/assets/clinic-partners.png" testid="clinic-partners-image"
                alt="A community pharmacy owner meeting a healthcare partner to discuss a clinic partnership"
                className="w-full h-[260px] sm:h-[320px] object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* 11 — REGISTRATION FORM */}
      <section id="register" className="py-20 sm:py-28 lg:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#F59E0B]">Register your interest</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C]">
              Register Your Interest in NCS Clinic
            </h2>
            <p className="mt-4 text-base text-[#475569] max-w-xl mx-auto">
              Whether you're a community pharmacy looking to expand your services or an independent
              prescribing pharmacist planning your own clinic, we'd love to hear from you.
            </p>
            <a href="/assets/ncs-clinic-prospectus.pdf" download="NCS-Clinic-Founding-Partner-Prospectus.pdf"
              data-testid="clinic-prospectus-btn-register"
              className="mt-5 inline-flex items-center gap-2.5 text-sm font-bold text-[#0B192C] border border-[#0B192C]/25 px-6 py-3.5 rounded-full transition-colors duration-300 hover:bg-[#0B192C] hover:text-white">
              <Download size={15} className="text-[#06B6D4]" /> Read the Founding Partner Prospectus first (PDF)
            </a>
          </Reveal>
          <Reveal>
            <ClinicForm key={preselectValue || "default"} applicantType={preselectValue} />
          </Reveal>
        </div>
      </section>

      {/* 12 — CLINICAL SAFETY & REGULATORY POSITION */}
      <section className="pb-20 sm:pb-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div data-testid="clinic-regulatory"
              className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-3xl p-8 sm:p-10">
              <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#0B192C]">
                <Lock size={14} className="text-[#06B6D4]" /> Clinical safety & regulatory position
              </p>
              <p className="mt-4 text-sm leading-relaxed text-[#475569]">
                NCS Clinic is currently a development project. Clinical services will be introduced only
                when the necessary professional, operational and regulatory arrangements are in place.
                Each service and location will require an appropriate assessment of:
              </p>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5">
                {REGULATORY.map((r) => (
                  <p key={r} className="flex items-start gap-2.5 text-xs leading-relaxed text-[#475569]">
                    <Check size={13} className="text-[#06B6D4] shrink-0 mt-0.5" /> {r}
                  </p>
                ))}
              </div>
              <p className="mt-5 text-xs leading-relaxed text-[#94A3B8]">
                Higher-risk services — including ADHD pathways, TRT and medical weight management — are
                not currently bookable and will only be promoted once the necessary clinical and
                regulatory arrangements are confirmed. Not every proposed treatment will be deliverable
                by every independent prescribing pharmacist.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default NcsClinic;
